import { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";

type Props = {
  count?: number;
  /** delay before the burst starts, ms */
  startDelay?: number;
  /** gap between each star pop, ms */
  stagger?: number;
  className?: string;
};

/**
 * Epic 5-star reveal: waits until in view, then bams stars in one by one
 * with scale + glow + tiny shake. Loops idle-shimmer after landing.
 */
export function StarBurst({
  count = 5,
  startDelay = 350,
  stagger = 280,
  className,
}: Props) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState<number>(0);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            io.disconnect();
            const timers: number[] = [];
            for (let i = 0; i < count; i++) {
              timers.push(
                window.setTimeout(
                  () => setVisible((n) => Math.max(n, i + 1)),
                  startDelay + i * stagger,
                ),
              );
            }
            timers.push(
              window.setTimeout(() => {
                setFlash(true);
                window.setTimeout(() => setFlash(false), 700);
              }, startDelay + count * stagger),
            );
            return () => timers.forEach((t) => window.clearTimeout(t));
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [count, startDelay, stagger]);

  return (
    <span
      ref={ref}
      className={`inline-flex items-center gap-[3px] align-middle sm:gap-[6px] ${className ?? ""}`}
      aria-label={`${count} av ${count} i betyg`}
    >
      {Array.from({ length: count }).map((_, i) => {
        const on = i < visible;
        return (
          <span
            key={i}
            className="relative inline-block"
            style={{
              transform: on ? "scale(1) rotate(0deg)" : "scale(0.2) rotate(-25deg)",
              opacity: on ? 1 : 0,
              transition:
                "transform 620ms cubic-bezier(.2,1.6,.35,1), opacity 360ms ease-out",
              filter: on
                ? `drop-shadow(0 0 14px rgba(255,196,90,${flash ? 0.95 : 0.6}))`
                : "none",
            }}
          >
            <Star
              strokeWidth={2}
              className="h-[18px] w-[18px] fill-[#ffcc4d] text-[#ffb020] sm:h-[28px] sm:w-[28px] md:h-[38px] md:w-[38px]"
            />

            {on && (
              <span
                className="pointer-events-none absolute inset-0 -z-10 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,204,77,0.55) 0%, rgba(255,204,77,0) 65%)",
                  animation: "star-burst-pulse 700ms ease-out both",
                }}
              />
            )}
          </span>
        );
      })}

      <style>{`
        @keyframes star-burst-pulse {
          0%   { transform: scale(0.4); opacity: 0.9; }
          60%  { transform: scale(1.8); opacity: 0.35; }
          100% { transform: scale(2.4); opacity: 0; }
        }
      `}</style>
    </span>
  );
}
